import { Business, User, Review, UserInterest, InterestCategory } from '../types';

export interface FilteringOptions {
  userInterests: UserInterest[];
  maxDistance?: number;
  minimumRating?: number;
  priceRange?: string[];
  showVerifiedOnly?: boolean;
  userLocation?: { lat: number; lng: number };
}

export interface PersonalizedFeed {
  businesses: Business[];
  reviews: Review[];
  recommendationScore: number;
  matchedInterests: string[];
}

const normalizeSearchTokens = (value: string): string[] =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .flatMap(token => token.endsWith('s') && token.length > 3 ? [token, token.slice(0, -1)] : [token]);

export class FilteringEngine {
  /**
   * Calculate relevance score for a business based on user interests
   */
  static calculateBusinessRelevance(business: Business, userInterests: UserInterest[]): number {
    if (userInterests.length === 0) return 1; // Show all if no interests selected

    let totalScore = 0;
    let matchedInterests = 0;

    // Check direct category matches
    userInterests.forEach(interest => {
      if ((business.interestCategories || []).includes(interest.category)) {
        totalScore += interest.weight * 2; // Direct category match gets 2x weight
        matchedInterests++;
      }
    });

    // Check tag matches
    userInterests.forEach(interest => {
      const interestKeywords = normalizeSearchTokens(interest.name);
      const businessTags = (business.tags || []).map(tag => tag.toLowerCase());
      const businessName = business.name.toLowerCase();
      const businessDescription = business.description?.toLowerCase() || '';

      interestKeywords.forEach(keyword => {
        if (businessTags.some(tag => tag.includes(keyword)) ||
            businessName.includes(keyword) ||
            businessDescription.includes(keyword)) {
          totalScore += interest.weight * 0.5; // Tag match gets 0.5x weight
        }
      });
    });

    // Bonus for high-rated businesses
    if (business.averageRating && business.averageRating >= 4.5) {
      totalScore += 1;
    }

    // Bonus for businesses with many reviews (popular)
    if (business.totalReviews && business.totalReviews >= 50) {
      totalScore += 0.5;
    }

    // Normalize score (0-10 scale)
    const maxPossibleScore = userInterests.reduce((sum, interest) => sum + interest.weight * 2, 0) + 1.5;
    return Math.min(10, (totalScore / maxPossibleScore) * 10);
  }

  /**
   * Calculate distance between two points in miles
   */
  static calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  /**
   * Filter and sort businesses based on user preferences
   */
  static filterBusinesses(businesses: Business[], options: FilteringOptions): Business[] {
    let filtered = businesses.filter(business => {
      // Interest filter — a business passes if it matches at least one user interest
      // via category OR via a keyword match in name / description / tags.
      // This prevents perfectly relevant businesses from being hidden just because
      // their interestCategories string differs slightly from the interest category key.
      if (options.userInterests && options.userInterests.length > 0) {
        const userCats = new Set(options.userInterests.map(i => i.category));
        const hasDirectMatch = (business.interestCategories || []).some(cat => userCats.has(cat));

        if (!hasDirectMatch) {
          // Fall back to keyword matching across interest names
          const interestKeywords = options.userInterests
            .flatMap(i => normalizeSearchTokens(i.name));
          const searchTarget = [
            business.name,
            business.description || '',
            ...(business.tags || []),
            business.category,
          ].join(' ').toLowerCase();
          const searchTokens = new Set(normalizeSearchTokens(searchTarget));
          const hasKeywordMatch = interestKeywords.some(kw =>
            kw.length > 2 && (searchTokens.has(kw) || searchTarget.includes(kw))
          );
          if (!hasKeywordMatch) {
            return false;
          }
        }
      }

      // Rating filter
      if (options.minimumRating && business.averageRating && business.averageRating < options.minimumRating) {
        return false;
      }

      // Price range filter
      if (options.priceRange && options.priceRange.length > 0 && business.priceRange) {
        if (!options.priceRange.includes(business.priceRange)) {
          return false;
        }
      }

      // Distance filter
      if (options.maxDistance && options.userLocation) {
        const distance = this.calculateDistance(
          options.userLocation.lat,
          options.userLocation.lng,
          business.latitude,
          business.longitude
        );
        if (distance > options.maxDistance) {
          return false;
        }
      }

      return true;
    });

    // Calculate relevance scores and sort
    const businessesWithScores = filtered.map(business => ({
      ...business,
      relevanceScore: this.calculateBusinessRelevance(business, options.userInterests)
    }));

    // Sort by relevance score (highest first), then by rating, then by review count
    return businessesWithScores.sort((a, b) => {
      if (b.relevanceScore !== a.relevanceScore) {
        return b.relevanceScore - a.relevanceScore;
      }
      if ((b.averageRating || 0) !== (a.averageRating || 0)) {
        return (b.averageRating || 0) - (a.averageRating || 0);
      }
      return (b.totalReviews || 0) - (a.totalReviews || 0);
    });
  }

  /**
   * Generate personalized feed for user
   */
  static generatePersonalizedFeed(
    businesses: Business[],
    reviews: Review[],
    options: FilteringOptions
  ): PersonalizedFeed {
    const filteredBusinesses = this.filterBusinesses(businesses, options);
    
    // Get reviews for top businesses
    const topBusinessIds = filteredBusinesses.slice(0, 20).map(b => b.id);
    const relevantReviews = reviews.filter(review => 
      topBusinessIds.includes(review.businessId)
    ).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    // Calculate overall recommendation score
    const totalRelevance = filteredBusinesses.reduce((sum, business) => 
      sum + ((business as any).relevanceScore || 0), 0
    );
    const recommendationScore = filteredBusinesses.length > 0 ? 
      totalRelevance / filteredBusinesses.length : 0;

    // Find matched interests
    const matchedInterests = options.userInterests
      .filter(interest => 
        filteredBusinesses.some(business =>
          (business.interestCategories || []).includes(interest.category)
        )
      )
      .map(interest => interest.name);

    return {
      businesses: filteredBusinesses,
      reviews: relevantReviews.slice(0, 10),
      recommendationScore,
      matchedInterests
    };
  }

  /**
   * Get interest-based recommendations
   */
  static getRecommendations(
    businesses: Business[],
    userInterests: UserInterest[],
    currentBusinessId?: string
  ): Business[] {
    const filtered = businesses.filter(business => business.id !== currentBusinessId);
    
    const options: FilteringOptions = {
      userInterests,
      minimumRating: 4.0 // Only recommend highly rated businesses
    };

    return this.filterBusinesses(filtered, options).slice(0, 5);
  }

  /**
   * Analyze user interests and provide insights
   */
  static analyzeUserInterests(userInterests: UserInterest[]): {
    topCategories: InterestCategory[];
    diversityScore: number;
    recommendations: string[];
  } {
    if (userInterests.length === 0) {
      return {
        topCategories: [],
        diversityScore: 0,
        recommendations: []
      };
    }

    // Group by category and calculate total weights
    const categoryWeights: Record<InterestCategory, number> = {} as any;
    userInterests.forEach(interest => {
      categoryWeights[interest.category] = (categoryWeights[interest.category] || 0) + interest.weight;
    });

    // Get top categories
    const topCategories = Object.entries(categoryWeights)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([category]) => category as InterestCategory);

    // Calculate diversity score (0-1)
    const uniqueCategories = new Set(userInterests.map(i => i.category)).size;
    const totalCategories = 16; // Total available categories
    const diversityScore = uniqueCategories / totalCategories;

    // Generate recommendations based on gaps
    const recommendations: string[] = [];
    if (diversityScore < 0.3) {
      recommendations.push("Consider exploring more categories to get better recommendations");
    }
    if (userInterests.every(i => i.weight <= 3)) {
      recommendations.push("Increase the importance of your favorite interests for better matching");
    }
    if (userInterests.length < 5) {
      recommendations.push("Add more interests to discover businesses you'll love");
    }

    return {
      topCategories,
      diversityScore,
      recommendations
    };
  }
}

export default FilteringEngine;

